import { useParams } from 'react-router-dom';
import { useValidateSignatureToken, getTokenStatus } from '../services/hooks/useSignatureToken';
import { ValidateTokenSuccessResponse } from '../types/signatureToken';
import SignatureFormView from '../components/contratos/SignatureFormView';

// ========================================
// COMPONENTE PRINCIPAL
// ========================================

export const SignatureTokenPage = () => {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, isError } = useValidateSignatureToken(token);
  const status = getTokenStatus(isLoading, isError, data);

  // Renderizar vistas según estado del token
  if (status === 'loading') return <LoadingView />;
  if (status === 'expired') return <ExpiredTokenView />;
  if (status === 'used') return <UsedTokenView />;
  if (status === 'notfound') return <NotFoundTokenView />;
  if (status === 'error') return <ErrorView />;

  // Token válido - mostrar formulario de firma
  return <SignatureFormView data={(data as ValidateTokenSuccessResponse).data} />;
};

// ========================================
// VISTAS DE ESTADO
// ========================================

/**
 * Vista de carga mientras se valida el token
 */
const LoadingView = () => {
    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-4">
        <div className="max-w-md mx-auto mt-20">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 w-full text-center">
            <div className="flex justify-center mb-4">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
            Validando enlace de firma...
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
            Por favor, espere un momento
            </p>
        </div>
        </div>
        </div>
    );
};

/**
 * Vista cuando el token ha expirado
 */
const ExpiredTokenView = () => {
    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-4">
        <div className="max-w-md mx-auto mt-20">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 w-full text-center">
            <div className="flex justify-center mb-4">
            <svg className="w-14 h-14 sm:w-16 sm:h-16 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
            Enlace Expirado
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            Este enlace de firma ha expirado y ya no es válido.
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
            Por favor, contacte con nuestro equipo para solicitar un nuevo enlace de firma.
            </p>
            <button
            onClick={() => window.location.href = 'mailto:info@bienesys.com'}
            className="w-full sm:w-auto bg-blue-600 text-white px-4 sm:px-6 py-2 text-sm sm:text-base rounded-lg hover:bg-blue-700 transition"
            >
            Contactar Soporte
            </button>
        </div>
        </div>
        </div>
    );
};

/**
 * Vista cuando el token ya fue usado
 */
const UsedTokenView = () => {
    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-4">
        <div className="max-w-md mx-auto mt-20">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 w-full text-center">
            <div className="flex justify-center mb-4">
            <svg className="w-14 h-14 sm:w-16 sm:h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
            Documento Ya Firmado
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            Este documento ya ha sido firmado exitosamente.
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
            Recibirá una copia del documento firmado en su correo electrónico.
            </p>
            <button
            onClick={() => window.close()}
            className="w-full sm:w-auto bg-gray-600 text-white px-4 sm:px-6 py-2 text-sm sm:text-base rounded-lg hover:bg-gray-700 transition"
            >
            Cerrar Ventana
            </button>
        </div>
        </div>
        </div>
    );
};

/**
 * Vista cuando el token no existe o es inválido
 */
const NotFoundTokenView = () => {
    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-4">
        <div className="max-w-md mx-auto mt-20">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 w-full text-center">
            <div className="flex justify-center mb-4">
            <svg className="w-14 h-14 sm:w-16 sm:h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
            Enlace Inválido
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            El enlace de firma no es válido o no existe.
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
            Verifique que haya copiado correctamente el enlace del correo electrónico.
            </p>
            <button
            onClick={() => window.location.href = 'mailto:info@bienesys.com'}
            className="w-full sm:w-auto bg-blue-600 text-white px-4 sm:px-6 py-2 text-sm sm:text-base rounded-lg hover:bg-blue-700 transition"
            >
            Contactar Soporte
            </button>
        </div>
        </div>
        </div>
    );
};

/**
 * Vista de error general del servidor
 */
const ErrorView = () => {
    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-4">
        <div className="max-w-md mx-auto mt-20">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 w-full text-center">
            <div className="flex justify-center mb-4">
            <svg className="w-14 h-14 sm:w-16 sm:h-16 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
            Error del Servidor
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            Ha ocurrido un error al validar el enlace.
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
            Por favor, intente nuevamente más tarde o contacte con soporte.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
            <button
                onClick={() => window.location.reload()}
                className="w-full sm:w-auto bg-gray-600 text-white px-4 sm:px-6 py-2 text-sm sm:text-base rounded-lg hover:bg-gray-700 transition"
            >
                Reintentar
            </button>
            <button
                onClick={() => window.location.href = 'mailto:info@bienesys.com'}
                className="w-full sm:w-auto bg-blue-600 text-white px-4 sm:px-6 py-2 text-sm sm:text-base rounded-lg hover:bg-blue-700 transition"
            >
                Contactar Soporte
            </button>
            </div>
        </div>
        </div>
        </div>
    );
};
